import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {formatMoney} from '@shopify/theme-currency';

import QuantitySelector from '../quantity-selector';
import {addToWishList, removeFromWishList, fetchItems} from '../../../../assets/scripts/utils/wishlist';
import {imageUrl} from '../../../../assets/scripts/utils/shopify-images';
import iconClose from '../../../../assets/svg/icon-cross.svg';
import iconHeart from '../../../../assets/svg/icon-heart.svg';
import { isDiscountMessageActive } from '../../../utils/discounts';

const CartItem = ({item, customData, updateLineItems, isCartPage, setGwpManuallyRemoved}) => {
  const [isWishlisted, setIsWishlisted] = useState(null);
  const [displayQtyLimitMessage, setDisplayQtyLimitMessage] = useState(false);

  const getStrikethroughPrice = () => {
    if (customData?.compare_at_price && item.final_price < customData.compare_at_price) {
      return customData.compare_at_price;
    }

    if (item.final_price < item.original_price) {
      return item.original_price;
    }

    return undefined;
  };

  const strikethroughPrice = getStrikethroughPrice();
  const price = item.final_price;
  const linePrice = price * item.quantity;

  const maxQty =
    window.theme.cartQtyLimitEnabled && item.properties && '_quantity_limit' in item.properties
      ? Number(item.properties._quantity_limit)
      : null;

  const finalSaleLabel = window.theme?.strings?.product?.finalSaleLabel;

  const wishlistIsEnabled = window.theme.wishlistEnabled;

  const wishlistProductData = {
    epi: item.id,
    du: `${window.location.origin}/products/${item.handle}`,
    empi: item.product_id,
    pr: item.final_price,
  };

  const handleWishlistBtnClick = () => {
    if (isWishlisted) {
      removeFromWishList(wishlistProductData);
      setIsWishlisted(false);
      return;
    }

    addToWishList(wishlistProductData);
    setIsWishlisted(true);
  };

  const handleItemRemove = () => {
    const isGWPItem = () => item.discounts.some((discount) => discount.title === 'GWP');
    if (isGWPItem()) {
      setGwpManuallyRemoved(true);
    }
    updateLineItems({[item.key]: 0});
  };

  useEffect(async () => {
    if (!isCartPage || !wishlistIsEnabled) {
      return;
    }

    const wishlistItems = await fetchItems();
    const isThisWishlisted = wishlistItems.some((wishlistItem) => Number(wishlistItem.epi) === Number(item.id));
    setIsWishlisted(isThisWishlisted);
  }, []);

  const DiscountMessage = () => {
    if (!isDiscountMessageActive(customData?.discountMessage) || customData?.discountMessage?.text === '') {
      return <></>;
    }

    return (
      <p className="cart-item__discount-message text-small discount-message" style={{color: customData.discountMessage.color}}>
        {customData.discountMessage.text}
      </p>
    )
  };

  return (
    <li className="cart-item">
      <div className="cart-item__content-wrapper">
        {item?.featured_image?.url && (
          <div className="cart-item__image">
            <a href={item.url}>
              <img
                src={imageUrl(item.featured_image.url, 276, 184, 'center')}
                width={138}
                height={92}
                alt={item.title}
                loading="lazy"
              />
            </a>
          </div>
        )}
        <div className="cart-item__content">
          <div className="cart-item__content-group">
            <div className="cart-item__content-inner">
              <p className="cart-item__title h7">{item.product_title}</p>
              <p className="cart-item__description text-small">{item.variant_title}</p>
              <DiscountMessage />
            </div>

            <p className="cart-item__price">
              {Boolean(strikethroughPrice) && strikethroughPrice > price && (
                <s data-compare-price>{formatMoney(strikethroughPrice, window.theme.moneyFormat)}</s>
              )}
              {price <= 0 ? 'FREE' : formatMoney(price, window.theme.moneyFormat)}
            </p>

            {isCartPage && linePrice > 0 && (
              <>
                <QuantitySelector
                  item={item}
                  maxQty={maxQty}
                  updateLineItems={updateLineItems}
                  setDisplayQtyLimitMessage={setDisplayQtyLimitMessage}
                />
                <p className="cart-item__price cart-item__price-total small-down--hide">
                  {formatMoney(linePrice, window.theme.moneyFormat)}
                </p>
              </>
            )}
          </div>

          <div className="cart-item__content-bottom">
            {!isCartPage && linePrice > 0 && (
              <QuantitySelector
                item={item}
                maxQty={maxQty}
                updateLineItems={updateLineItems}
                setDisplayQtyLimitMessage={setDisplayQtyLimitMessage}
              />
            )}

            <button className="cart-item__remove h9 btn-none" onClick={handleItemRemove} type="button">
              <span>{window.theme.strings.remove}</span>
              <span dangerouslySetInnerHTML={{__html: iconClose}} />
            </button>

            {isCartPage && wishlistIsEnabled && (
              <button
                class={`btn-none${isWishlisted ? ' is-wishlisted' : ''}`}
                type="button"
                aria-label={isWishlisted ? window.theme.strings.wishlist.remove : window.theme.strings.wishlist.add}
                data-wishlist-add
                dangerouslySetInnerHTML={{__html: iconHeart}}
                onClick={handleWishlistBtnClick}
              />
            )}
          </div>
        </div>

        {displayQtyLimitMessage && (
          <p className="quantity-selector__error">
            {window.theme?.strings?.quantityLimit.replace('{{ number }}', maxQty)}
          </p>
        )}

        {item.properties?.[finalSaleLabel] && (
          <p className="cart-item__final-sale">
            {finalSaleLabel}: {item.properties[finalSaleLabel]}
          </p>
        )}
      </div>
    </li>
  );
};

CartItem.propTypes = {
  item: PropTypes.object,
  customData: PropTypes.object,
  updateLineItems: PropTypes.func,
  isCartPage: PropTypes.bool,
  setGwpManuallyRemoved: PropTypes.func,
};

export default CartItem;
